/**
 * Collection Task / Material / IIR API client.
 */
import axios from 'axios';

const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';

/** Thrown when a Collected Material file upload's content matches a file the caller already
 * has in My files (see CollectionMaterialDuplicateUploadException on the backend). Mirrors
 * SourceUploadDuplicateError (sourcesApi.js) but also carries `collectionTaskId`. */
export class CollectionMaterialUploadDuplicateError extends Error {
    constructor(referenceId, message = 'This file is already in My files.', collectionTaskId = null) {
        super(message);
        this.name = 'CollectionMaterialUploadDuplicateError';
        this.referenceId = referenceId;
        this.collectionTaskId = collectionTaskId;
    }
}

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return token
        ? {'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'}
        : {'Content-Type': 'application/json'};
}

async function apiFetch(path, opts = {}) {
    const resp = await fetch(`${BASE}${path}`, {headers: authHeaders(), ...opts});
    if (resp.status === 404) return null;
    if (!resp.ok) {
        let detail = `${opts.method || 'GET'} ${path} → ${resp.status}`;
        try {
            const body = await resp.json();
            if (body?.error) detail = body.error;
            else if (body?.message) detail = body.message;
        } catch {
            /* ignore */
        }
        throw new Error(detail);
    }
    if (resp.status === 204) return null;
    return resp.json();
}

/**
 * Latest persisted body (ProseMirror JSON snapshot) of an IIR report.
 * Submitting an IIR forces a snapshot persist, and the submit-time NER pipeline
 * re-persists after applying wikidataEntity marks — so the latest version carries
 * the recognized/linked entity mentions. Returns {version, pmJson} or null.
 */
export async function getIirLatestBody(reportId) {
    const versions = await apiFetch(`/api/reports/${encodeURIComponent(reportId)}/versions`);
    if (!Array.isArray(versions) || versions.length === 0) return null;
    const latest = versions[0]?.version; // listVersions orders version DESC
    if (latest == null) return null;
    const body = await apiFetch(`/api/reports/${encodeURIComponent(reportId)}/versions/${encodeURIComponent(latest)}`);
    if (!body || !body.pmJson) return null;
    return {version: body.version, pmJson: body.pmJson};
}

/** Persist the current PM JSON snapshot before IIR submit/modify when Yjs is lagging. */
export async function persistReportPmJson(reportId, pmJson) {
    if (!reportId || !pmJson) return null;
    const versions = await apiFetch(`/api/reports/${encodeURIComponent(reportId)}/versions`);
    const latest = Array.isArray(versions) && versions.length > 0 ? versions[0]?.version : null;
    const nextVersion = typeof latest === 'number' ? latest + 1 : 1;
    return apiFetch(`/api/reports/${encodeURIComponent(reportId)}/versions`, {
        method: 'POST',
        body: JSON.stringify({
            version: nextVersion,
            pmJson,
        }),
    });
}

export async function listCollectionTasks({q, status, assigneeId, icrId, limit} = {}) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (status) params.set('status', status);
    if (assigneeId) params.set('assigneeId', assigneeId);
    if (icrId) params.set('icrId', icrId);
    if (limit) params.set('limit', String(limit));
    const qs = params.toString();
    const data = await apiFetch(`/api/collection-tasks${qs ? `?${qs}` : ''}`);
    return Array.isArray(data) ? data : [];
}

export async function getCollectionTask(idOrNumber) {
    return apiFetch(`/api/collection-tasks/${encodeURIComponent(idOrNumber)}`);
}

function normalizeTargetsPayload(payload) {
    if (!payload || !Array.isArray(payload.targets)) return payload;
    return {
        ...payload,
        targets: payload.targets
            .map((t) => (typeof t === 'string' ? t : t?.entityRef))
            .filter(Boolean),
    };
}

export async function createCollectionTask(payload) {
    return apiFetch('/api/collection-tasks', {
        method: 'POST',
        body: JSON.stringify(normalizeTargetsPayload(payload)),
    });
}

export async function updateCollectionTask(idOrNumber, payload) {
    return apiFetch(`/api/collection-tasks/${encodeURIComponent(idOrNumber)}`, {
        method: 'PUT',
        body: JSON.stringify(normalizeTargetsPayload(payload)),
    });
}

export async function transitionCollectionTask(idOrNumber, status, statusComment) {
    return apiFetch(`/api/collection-tasks/${encodeURIComponent(idOrNumber)}/status`, {
        method: 'POST',
        body: JSON.stringify({status, statusComment}),
    });
}

/** CM/admin assign or reassign a task to a collector (immediate, no confirmation). */
export async function assignCollectionTask(idOrNumber, assigneeId) {
    return apiFetch(`/api/collection-tasks/${encodeURIComponent(idOrNumber)}/assign`, {
        method: 'POST',
        body: JSON.stringify({assigneeId}),
    });
}

export async function claimCollectionTask(idOrNumber) {
    return apiFetch(`/api/collection-tasks/${encodeURIComponent(idOrNumber)}/claim`, {
        method: 'POST',
        body: JSON.stringify({}),
    });
}

export async function deleteCollectionTask(idOrNumber) {
    return apiFetch(`/api/collection-tasks/${encodeURIComponent(idOrNumber)}`, {
        method: 'DELETE',
    });
}

export async function autofillCollectionTask({icrIds = [], eeiIds = []} = {}) {
    return apiFetch('/api/collection-tasks/autofill', {
        method: 'POST',
        body: JSON.stringify({icrIds, eeiIds}),
    });
}

/**
 * Similarity-related ICR/EEIs for a Collection Task selection (CM/admin).
 */
export async function fetchRelatedFromSelection({icrIds = [], eeiIds = [], overlapMinPercent} = {}) {
    const body = {icrIds, eeiIds};
    if (overlapMinPercent != null) body.overlapMinPercent = overlapMinPercent;
    return apiFetch('/api/similarity/related-from-selection', {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

export async function listCollectionMaterials({taskId, limit} = {}) {
    const params = new URLSearchParams();
    if (taskId) params.set('taskId', taskId);
    if (limit) params.set('limit', String(limit));
    const qs = params.toString();
    const data = await apiFetch(`/api/collection/materials${qs ? `?${qs}` : ''}`);
    return Array.isArray(data) ? data : [];
}

export async function getCollectionMaterial(id) {
    return apiFetch(`/api/collection/materials/${encodeURIComponent(id)}`);
}

export async function createCollectionMaterial(payload) {
    return apiFetch('/api/collection/materials', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

/** Multipart file upload → Collected Material linked to a DataItem. */
export async function createCollectionMaterialFromFile({
    collectionTaskId,
    file,
    title,
    collectionMethod,
    classificationLevel,
    distribution,
    compartment,
    signal,
    onProgress,
} = {}) {
    if (!file) throw new Error('file is required');
    if (!collectionTaskId) throw new Error('collectionTaskId is required');
    const form = new FormData();
    form.append('file', file);
    form.append('collectionTaskId', collectionTaskId);
    if (title) form.append('title', title);
    if (collectionMethod) form.append('collectionMethod', collectionMethod);
    if (classificationLevel) form.append('classificationLevel', classificationLevel);
    if (Array.isArray(distribution)) {
        distribution.forEach((value) => form.append('distribution', value));
    }
    if (Array.isArray(compartment)) {
        compartment.forEach((value) => form.append('compartment', value));
    }

    const token = localStorage.getItem('access_token');
    const headers = token ? {Authorization: `Bearer ${token}`} : {};
    try {
        const response = await axios.post(`${BASE}/api/collection/materials/upload`, form, {
            headers,
            signal,
            onUploadProgress: (progressEvent) => {
                if (typeof onProgress !== 'function') return;
                const total = progressEvent.total || file.size || 0;
                const loaded = Math.min(progressEvent.loaded || 0, total || progressEvent.loaded || 0);
                const progress = total > 0 ? Math.round((loaded / total) * 100) : 0;
                onProgress({loaded, total: total || null, progress});
            },
        });
        return response.data;
    } catch (error) {
        if (axios.isCancel(error) || error?.name === 'AbortError' || error?.name === 'CanceledError') {
            const cancelError = new Error('Upload canceled');
            cancelError.name = 'AbortError';
            throw cancelError;
        }
        const status = error?.response?.status;
        const data = error?.response?.data;
        if (status === 409 && data?.referenceId != null && data?.duplicate) {
            throw new CollectionMaterialUploadDuplicateError(
                data.referenceId,
                data.message || 'This file is already in My files.',
                data.collectionTaskId || collectionTaskId,
            );
        }
        const responseMessage = typeof data === 'string' ? data : data?.message;
        throw new Error(responseMessage || error?.message || `POST /api/collection/materials/upload → ${status || 'network error'}`);
    }
}

/**
 * The caller's own in-flight (pending/processing) or recently-finished (last 2h) file-upload
 * Collection Materials. Used by the Upload Queue panel to hydrate on mount / poll for pipeline
 * stage progress so in-progress uploads survive refreshes and task navigation. Mirrors Sources'
 * fetchUploadQueue (sourcesApi.js) against the CM-specific endpoint.
 */
export async function fetchCollectionMaterialUploadQueue({taskId, signal} = {}) {
    const params = new URLSearchParams();
    if (taskId) params.set('taskId', taskId);
    const qs = params.toString();
    return apiFetch(`/api/collection/materials/upload/queue${qs ? `?${qs}` : ''}`, {signal}) || [];
}

export async function updateCollectionMaterial(id, payload) {
    return apiFetch(`/api/collection/materials/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
}

/**
 * Updates the Classification Level/Distribution/Compartment on the material's linked DataItem.
 * Reuses the generic material-update PUT — `CollectionMaterialService.update()` forwards the
 * `classification` key to `DataItemService.update()` on the canonical DataItem, same path
 * Sources uses. Only updates this material's own item, matching the existing
 * title/collectionMethod edit semantics (single-row, no propagation).
 */
export async function updateCollectionMaterialClassification(id, {level, distribution, compartment} = {}) {
    return updateCollectionMaterial(id, {
        classification: {
            level: level || 'U',
            distribution: Array.isArray(distribution) ? distribution : [],
            compartment: Array.isArray(compartment) ? compartment : [],
        },
    });
}

export async function submitCollectionMaterial(id) {
    return apiFetch(`/api/collection/materials/${encodeURIComponent(id)}/submit`, {
        method: 'POST',
        body: JSON.stringify({}),
    });
}

export async function modifyCollectionMaterial(id) {
    return apiFetch(`/api/collection/materials/${encodeURIComponent(id)}/modify`, {
        method: 'POST',
        body: JSON.stringify({}),
    });
}

export async function deleteCollectionMaterial(id) {
    return apiFetch(`/api/collection/materials/${encodeURIComponent(id)}`, {
        method: 'DELETE',
    });
}

export async function listCollectionIirs({taskId, limit} = {}) {
    const params = new URLSearchParams();
    if (taskId) params.set('taskId', taskId);
    if (limit) params.set('limit', String(limit));
    const qs = params.toString();
    const data = await apiFetch(`/api/collection/iirs${qs ? `?${qs}` : ''}`);
    return Array.isArray(data) ? data : [];
}

export async function createCollectionIir({collectionTaskId, title} = {}) {
    const body = {collectionTaskId};
    if (title != null && String(title).trim()) {
        body.title = String(title).trim();
    }
    return apiFetch('/api/collection/iirs', {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

export async function getCollectionIir(id) {
    return apiFetch(`/api/collection/iirs/${encodeURIComponent(id)}`);
}

export async function submitCollectionIir(id) {
    return apiFetch(`/api/collection/iirs/${encodeURIComponent(id)}/submit`, {
        method: 'POST',
        body: JSON.stringify({}),
    });
}

export async function approveCollectionIir(id) {
    return apiFetch(`/api/collection/iirs/${encodeURIComponent(id)}/approve`, {
        method: 'POST',
        body: JSON.stringify({}),
    });
}

export async function modifyCollectionIir(id) {
    return apiFetch(`/api/collection/iirs/${encodeURIComponent(id)}/modify`, {
        method: 'POST',
        body: JSON.stringify({}),
    });
}
